/**
 * Shared markdown rendering utilities for chat messages.
 *
 * Single source of truth for sanitize schema, plugins, component overrides,
 * and text preprocessing used by ChatMessage, ProgressiveReveal, and StreamingMarkdown.
 */

import type { Components, Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

// =============================================================================
// Chat Message Type Union
// =============================================================================

/** All recognized chat message types across lobby and game stores. */
export type ChatMessageType =
  | 'user'
  | 'system'
  | 'error'
  | 'dm'
  | 'tavian'
  | 'whisper'
  | 'npc'
  | 'aldric'
  | 'kira'
  | 'grolt'
  | 'inner'
  | 'party'
  | 'success'
  | 'info'
  | 'warning'
  | 'party-join'
  | 'party-leave'
  // Tavian-voiced canned bubble with inline action button.
  // Currently only powers the in-chat low-balance gate (`buyTokens`
  // action). Mirrors mobile `ChatBubbleKind.action`. The ChatMessage
  // renderer reads `meta.action_kind` to decide what the button does.
  | 'token-gate';

// =============================================================================
// Sanitize Schema
// =============================================================================

export const chatSanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code || []), 'className'],
    pre: [...(defaultSchema.attributes?.pre || []), 'className'],
    span: [...(defaultSchema.attributes?.span || []), 'className'],
    div: [...(defaultSchema.attributes?.div || []), 'className'],
  },
};

// =============================================================================
// Plugin Arrays (stable references — never recreated between renders)
// =============================================================================

export const chatRemarkPlugins: Options['remarkPlugins'] = [remarkGfm];

export const chatRehypePlugins: Options['rehypePlugins'] = [[rehypeSanitize, chatSanitizeSchema]];

// =============================================================================
// Markdown Component Overrides
// =============================================================================

export const chatMarkdownComponents: Components = {
  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1 my-2">{children}</ul>,
  // Forward `start`/`value` so a numbered list that gets fractured into several
  // single-item <ol>s (react-markdown splits an ordered list interrupted by a
  // nested sub-bullet) keeps its real numbering instead of restarting every item
  // at "1." — otherwise e.g. all character-creation background options render "1."
  ol: ({ children, start }) => (
    <ol start={start ?? undefined} className="list-decimal pl-4 mb-2 space-y-1 my-2">
      {children}
    </ol>
  ),
  li: ({ children, value }) => (
    <li value={value} className="leading-relaxed">
      {children}
    </li>
  ),
  h1: ({ children }) => <h1 className="text-lg font-bold mb-2 mt-4">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold mb-2 mt-3">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-bold mb-1 mt-2">{children}</h3>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-primary/50 pl-4 italic my-2 text-muted-foreground">
      {children}
    </blockquote>
  ),
  code: ({ children, ...props }) => (
    <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono" {...props}>
      {children}
    </code>
  ),
  pre: ({ children }) => <>{children}</>,
  table: ({ children }) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full border-collapse border border-border">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-border">{children}</tr>,
  th: ({ children }) => (
    <th className="border border-border px-3 py-2 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border border-border px-3 py-2">{children}</td>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-4 hover:text-primary/80"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-border" />,
};

// =============================================================================
// Text Processing Utilities
// =============================================================================

/**
 * Strip fenced code blocks from chat content.
 * Removes complete ```...``` blocks (internal JSON/data not meant for display)
 * and any trailing unclosed fences (streaming artifact that causes dark block flash).
 */
export function stripCodeBlocks(text: string): string {
  let result = text.replace(/```[\s\S]*?```/g, '');
  const unclosedFence = result.indexOf('```');
  if (unclosedFence !== -1) {
    result = result.substring(0, unclosedFence);
  }
  return result.trimEnd();
}

/**
 * Preprocess content for chat rendering:
 *  1. Strip leading indentation from every line. LLM NPCs frequently indent
 *     list-like output (e.g. a 4-space-indented party roster), and CommonMark
 *     turns a blank line + 4-space indent into an *indented code block* — the
 *     whole block then renders as monospace with collapsed line breaks. Chat is
 *     always prose (fenced code is already removed by `stripCodeBlocks`), so an
 *     indented code block is never intended; dedenting kills that bug class.
 *  2. Convert single newlines into markdown hard breaks (`  \n`) so each line of
 *     a newline-separated list (one party member per line, etc.) renders on its
 *     own row instead of collapsing into one run-on paragraph.
 */
export function preprocessContent(text: string): string {
  return text
    .split('\n\n')
    .map((paragraph) =>
      paragraph
        .split('\n')
        .map((line) => line.replace(/^[ \t]+/, ''))
        .join('  \n')
    )
    .join('\n\n');
}
